import Phaser from "phaser";
import { GameEvents } from "../core/Events";
import { Player } from "../entities/player/Player";
import { SkeletonEnemy } from "../entities/enemies/SkeletonEnemy";
import { SkeletonKing } from "../entities/bosses/SkeletonKing";
import { PlayerProjectile } from "./PlayerProjectile";
import { SPELLS, spellByNumber, type SpellDefinition, type SpellId } from "./SpellDefinitions";

export class SpellController {
  private readonly cooldownUntil = new Map<SpellId, number>();
  private readonly projectiles: PlayerProjectile[] = [];
  private readonly numberKeys: Phaser.Input.Keyboard.Key[];
  private readonly pointerHandler: (pointer: Phaser.Input.Pointer) => void;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly player: Player,
    private readonly enemies: SkeletonEnemy[],
    private readonly boss: SkeletonKing,
  ) {
    this.numberKeys = [1, 2, 3, 4, 5, 6].map((number) =>
      scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.ONE + number - 1),
    );

    this.pointerHandler = (pointer) => {
      if (pointer.leftButtonDown()) this.cast(1, pointer.worldX, pointer.worldY);
      if (pointer.rightButtonDown()) this.cast(2, pointer.worldX, pointer.worldY);
    };
    scene.input.on("pointerdown", this.pointerHandler);
    scene.input.mouse?.disableContextMenu();

    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
  }

  update(time: number, delta: number): void {
    this.numberKeys.forEach((key, index) => {
      if (Phaser.Input.Keyboard.JustDown(key)) {
        const pointer = this.scene.input.activePointer;
        this.cast(index + 1, pointer.worldX, pointer.worldY);
      }
    });

    for (const projectile of this.projectiles) {
      if (!projectile.active) continue;
      projectile.update(delta);
      this.resolveProjectileHits(projectile);
    }

    for (const spell of SPELLS) {
      const remaining = Math.max(0, (this.cooldownUntil.get(spell.id) ?? 0) - time);
      this.scene.events.emit(
        GameEvents.SpellCooldownChanged,
        spell.id,
        remaining,
        spell.cooldownMs,
      );
    }
  }

  cast(number: number, targetX: number, targetY: number): void {
    const spell = spellByNumber(number);
    if (!spell || !this.player.active) return;

    const now = this.scene.time.now;
    if (now < (this.cooldownUntil.get(spell.id) ?? 0)) {
      this.scene.events.emit(GameEvents.SpellRejected, spell.id, "cooldown");
      return;
    }

    if (!this.player.spendMana(spell.mana)) {
      this.scene.events.emit(GameEvents.SpellRejected, spell.id, "mana");
      return;
    }

    this.cooldownUntil.set(spell.id, now + spell.cooldownMs);
    this.scene.events.emit(GameEvents.SpellRecognized, spell.id);

    const angle = Phaser.Math.Angle.Between(this.player.x, this.player.y, targetX, targetY);

    for (const enemy of this.enemies) {
      enemy.dodgeFrom(targetX, targetY, now);
    }

    switch (spell.id) {
      case "fireball":
      case "ice-spike":
        this.projectiles.push(
          new PlayerProjectile(this.scene, this.player.x, this.player.y - 8, angle, spell),
        );
        break;
      case "lightning":
        this.castLightning(spell, targetX, targetY);
        break;
      case "shield":
        this.player.activateShield(3500);
        break;
      case "heal":
        this.player.heal(280);
        this.createPulse(this.player.x, this.player.y, spell.tint, 90);
        break;
      case "teleport":
        this.createPulse(this.player.x, this.player.y, spell.tint, 70);
        this.player.teleportTo(targetX, targetY);
        this.createPulse(this.player.x, this.player.y, spell.tint, 95);
        break;
    }
  }

  private resolveProjectileHits(projectile: PlayerProjectile): void {
    for (const enemy of this.enemies) {
      if (!enemy.isAlive()) continue;
      if (Phaser.Math.Distance.Between(projectile.x, projectile.y, enemy.x, enemy.y) < 42) {
        if (!projectile.consume()) return;
        const angle = Phaser.Math.Angle.Between(this.player.x, this.player.y, enemy.x, enemy.y);
        enemy.damage(projectile.spell.damage, angle);
        this.createPulse(enemy.x, enemy.y, projectile.spell.tint, 48);
        return;
      }
    }

    if (
      !this.boss.isDefeated() &&
      Phaser.Math.Distance.Between(projectile.x, projectile.y, this.boss.x, this.boss.y) < 72
    ) {
      if (!projectile.consume()) return;
      this.boss.damage(projectile.spell.damage);
      this.createPulse(this.boss.x, this.boss.y, projectile.spell.tint, 62);
    }
  }

  private castLightning(spell: SpellDefinition, targetX: number, targetY: number): void {
    const targets = this.enemies
      .filter((enemy) => enemy.isAlive())
      .map((enemy) => ({
        enemy,
        distance: Phaser.Math.Distance.Between(targetX, targetY, enemy.x, enemy.y),
      }))
      .filter(({ distance }) => distance <= 250)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 4);

    if (
      !this.boss.isDefeated() &&
      Phaser.Math.Distance.Between(targetX, targetY, this.boss.x, this.boss.y) <= 260
    ) {
      this.drawLightning(this.player.x, this.player.y, this.boss.x, this.boss.y);
      this.boss.damage(spell.damage);
    }

    let fromX = this.player.x;
    let fromY = this.player.y;
    for (const { enemy } of targets) {
      this.drawLightning(fromX, fromY, enemy.x, enemy.y);
      enemy.damage(spell.damage);
      fromX = enemy.x;
      fromY = enemy.y;
    }

    if (targets.length === 0) {
      this.drawLightning(this.player.x, this.player.y, targetX, targetY);
    }
  }

  private drawLightning(x1: number, y1: number, x2: number, y2: number): void {
    const graphics = this.scene.add.graphics().setDepth(50_000);
    graphics.lineStyle(4, 0x8eb9ff, 0.95);
    graphics.beginPath();
    graphics.moveTo(x1, y1);
    const segments = 9;
    for (let i = 1; i < segments; i += 1) {
      const t = i / segments;
      graphics.lineTo(
        Phaser.Math.Linear(x1, x2, t) + Phaser.Math.Between(-18, 18),
        Phaser.Math.Linear(y1, y2, t) + Phaser.Math.Between(-18, 18),
      );
    }
    graphics.lineTo(x2, y2);
    graphics.strokePath();
    this.scene.tweens.add({
      targets: graphics,
      alpha: 0,
      duration: 180,
      onComplete: () => graphics.destroy(),
    });
  }

  private createPulse(x: number, y: number, color: number, radius: number): void {
    const ring = this.scene.add.circle(x, y, 12, color, 0.08)
      .setStrokeStyle(3, color, 0.85)
      .setDepth(50_000);
    this.scene.tweens.add({
      targets: ring,
      radius,
      alpha: 0,
      duration: 320,
      onComplete: () => ring.destroy(),
    });
  }

  private destroy(): void {
    this.scene.input.off("pointerdown", this.pointerHandler);
  }
}
